import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { chatApi, usersApi } from '../lib/api';
import LoadingScreen from '../components/common/LoadingScreen';
import EmptyState from '../components/common/EmptyState';
import TimeAgo from '../components/common/TimeAgo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  MessageSquare,
  Plus,
  Send,
  Users,
  Check,
  CheckCheck,
  Loader2,
  Hash,
} from 'lucide-react';
import { toast } from 'sonner';

const ChatPage = () => {
  const { user } = useAuth();
  const { socket, joinRoom, leaveRoom, emitTyping } = useSocket();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // New Room Dialog
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [creatingRoom, setCreatingRoom] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchRooms = async () => {
    try {
      const response = await chatApi.getRooms();
      setRooms(response.data);
      if (response.data.length > 0 && !activeRoom) {
        setActiveRoom(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await chatApi.getMessages(roomId);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    if (activeRoom) {
      fetchMessages(activeRoom.id);
      joinRoom(activeRoom.id);

      return () => {
        leaveRoom(activeRoom.id);
      };
    }
  }, [activeRoom?.id]);

  useEffect(() => {
    if (socket && activeRoom) {
      const handleNewMessage = (msg) => {
        if (msg.room_id === activeRoom.id) {
          setMessages(prev => [...prev, msg]);
          // Mark as read
          chatApi.markRead(msg.id).catch(console.error);
        }
      };

      const handleMessageRead = ({ message_id, user_id, read_by }) => {
        setMessages(prev => 
          prev.map(msg => 
            msg.id === message_id ? { ...msg, read_by } : msg
          )
        );
      };

      const handleUserTyping = ({ user_name }) => {
        if (user_name !== user?.name) {
          setTypingUsers(prev => {
            if (!prev.includes(user_name)) {
              return [...prev, user_name];
            }
            return prev;
          });
          setTimeout(() => {
            setTypingUsers(prev => prev.filter(u => u !== user_name));
          }, 3000);
        }
      };

      socket.on('new_message', handleNewMessage);
      socket.on('message_read', handleMessageRead);
      socket.on('user_typing', handleUserTyping);

      return () => {
        socket.off('new_message', handleNewMessage);
        socket.off('message_read', handleMessageRead);
        socket.off('user_typing', handleUserTyping);
      };
    }
  }, [socket, activeRoom?.id, user?.name]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeRoom) return;

    setSendingMessage(true);
    try {
      await chatApi.sendMessage({
        content: newMessage,
        room_id: activeRoom.id,
      });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleTyping = () => {
    if (activeRoom && user) {
      emitTyping(activeRoom.id, user.name);
    }
  };

  const handleCreateRoom = async () => {
    if (!roomName.trim()) return;

    setCreatingRoom(true);
    try {
      const response = await chatApi.createRoom({
        name: roomName,
        is_private: false,
        members: [],
      });
      setRooms(prev => [...prev, response.data]);
      setActiveRoom(response.data);
      setRoomDialogOpen(false);
      setRoomName('');
      toast.success('Chat room created');
    } catch (error) {
      console.error('Error creating room:', error);
      toast.error('Failed to create room');
    } finally {
      setCreatingRoom(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <LoadingScreen message="Loading chat..." />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-4 lg:p-6" data-testid="chat-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-headings text-3xl font-bold uppercase tracking-tight text-white">
            Personnel Chat
          </h1>
          <p className="text-zinc-400 mt-1">
            Secure inter-department communication
          </p>
        </div>
        <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-500 hover:bg-blue-600 text-white font-bold uppercase" data-testid="create-room-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Channel
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="font-headings text-xl uppercase">Create Channel</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Create a new chat channel for your team.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Channel Name</Label>
                <Input
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  placeholder="e.g., Fire Department"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="room-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRoomDialogOpen(false)}
                className="border-zinc-700 text-zinc-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateRoom}
                disabled={creatingRoom || !roomName.trim()}
                className="bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="create-room-submit"
              >
                {creatingRoom ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Chat Interface */}
      <div className="grid lg:grid-cols-4 gap-6 h-[calc(100vh-200px)]">
        {/* Rooms Sidebar */}
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
              <Hash className="w-5 h-5 text-blue-400" />
              Channels
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[calc(100vh-320px)]">
              {rooms.length === 0 ? (
                <EmptyState 
                  type="messages" 
                  title="No Channels"
                  description="Create a channel to start communicating with your team."
                  action={() => setRoomDialogOpen(true)}
                  actionLabel="Create Channel"
                />
              ) : (
                <div className="space-y-2">
                  {rooms.map((room) => (
                    <button
                      key={room.id}
                      onClick={() => setActiveRoom(room)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                        activeRoom?.id === room.id
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                      }`}
                      data-testid={`room-${room.id}`}
                    >
                      <Hash className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{room.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-3 flex flex-col">
          {activeRoom ? (
            <>
              {/* Chat Header */}
              <CardHeader className="pb-2 border-b border-zinc-800">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-headings text-lg uppercase tracking-wide text-white flex items-center gap-2">
                    <Hash className="w-5 h-5 text-blue-400" />
                    {activeRoom.name}
                  </CardTitle>
                  <Badge variant="outline" className="text-zinc-400 border-zinc-700">
                    <Users className="w-3 h-3 mr-1" />
                    {activeRoom.members?.length || 0} members
                  </Badge>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-[calc(100vh-400px)] p-4">
                  {messages.length === 0 ? (
                    <EmptyState type="messages" />
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, index) => {
                        const isOwn = msg.sender_id === user?.id;
                        const showAvatar = index === 0 || messages[index - 1]?.sender_id !== msg.sender_id;
                        
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}
                          >
                            {showAvatar ? (
                              <Avatar className="w-8 h-8 flex-shrink-0">
                                <AvatarFallback className={`text-xs ${isOwn ? 'bg-blue-500' : 'bg-zinc-700'}`}>
                                  {getInitials(msg.sender_name)}
                                </AvatarFallback>
                              </Avatar>
                            ) : (
                              <div className="w-8" />
                            )}
                            <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                              {showAvatar && (
                                <div className={`flex items-center gap-2 mb-1 ${isOwn ? 'justify-end' : ''}`}>
                                  <span className="text-xs font-medium text-zinc-400">
                                    {isOwn ? 'You' : msg.sender_name}
                                  </span>
                                  <span className="text-xs text-zinc-600">
                                    {formatTime(msg.created_at)}
                                  </span>
                                </div>
                              )}
                              <div
                                className={`p-3 rounded-lg ${
                                  isOwn
                                    ? 'bg-blue-500 text-white rounded-br-none'
                                    : 'bg-zinc-800 text-zinc-100 rounded-bl-none'
                                }`}
                              >
                                <p className="text-sm">{msg.content}</p>
                              </div>
                              {isOwn && (
                                <div className="flex justify-end mt-1">
                                  {msg.read_by?.length > 1 ? (
                                    <CheckCheck className="w-4 h-4 text-blue-400" />
                                  ) : (
                                    <Check className="w-4 h-4 text-zinc-500" />
                                  )}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}

                  {/* Typing Indicator */}
                  {typingUsers.length > 0 && (
                    <div className="flex items-center gap-2 mt-4 text-zinc-500 text-sm">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span>{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...</span>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>

              {/* Message Input */}
              <div className="p-4 border-t border-zinc-800">
                <form onSubmit={handleSendMessage} className="flex gap-3">
                  <Input
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      handleTyping();
                    }}
                    placeholder="Type a message..."
                    className="bg-zinc-800 border-zinc-700 text-white flex-1"
                    data-testid="message-input"
                  />
                  <Button
                    type="submit"
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">Select a channel to start chatting</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ChatPage;
